import { CSSProperties, useId, useMemo } from 'react';

import { Box, Stack, Tooltip, Typography } from '@mui/material';
import {
  GaugeContainer,
  GaugeReferenceArc,
  GaugeValueArc,
  useGaugeState,
} from '@mui/x-charts/Gauge';
import scssVariables from '@styles/variables.module.scss';
import cx from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import styles from './GaugeRealized.module.scss';
import { formatCurrency } from '@/hooks/useFormatCurrency/formatCurrency';
import { formatNumber } from '@/hooks/useFormatNumber/formatNumber';
import { LanguageCode } from '@/stores/locale';

interface GaugeRealizedProps {
  type: 'currency' | 'hours';
  /** Title of the gauge (e.g., "Project hours", "Project revenue") */
  title: string;
  /** Language code for formatting (e.g., 'en', 'fr') */
  lang: LanguageCode;
  /** Realized value as a number */
  realizedValue: number;
  /** Planned value as a number */
  plannedValue: number;
  /** Budget value - the maximum/target for the gauge */
  budgetValue: number;
  /** Currency code for formatting when type is 'currency' (e.g., 'USD', 'EUR') */
  currency?: string | null;
  /** Width of the gauge in pixels */
  width?: number;
  /** Height of the gauge in pixels */
  height?: number;
}

const GaugeValueText = ({
  isExceeded,
  isDash,
  realizedText,
}: {
  isExceeded: boolean;
  isDash: boolean;
  realizedText: string;
}) => {
  const { value, valueMax, cx: centerX, cy: centerY } = useGaugeState();

  if (value === null || valueMax === null) {
    return null;
  }

  const percentage = valueMax === 0 ? 0 : Math.round((value / valueMax) * 100);

  // Calculate font sizes based on gauge dimensions
  // Assuming cx and cy represent the center, the full size is 2x
  const gaugeSize = Math.min(centerX, centerY) * 2;
  const mainFontSize = Math.round(gaugeSize * 0.16);
  const subFontSize = Math.round(gaugeSize * 0.1);

  return (
    <text
      x="50%"
      y="50%"
      textAnchor="middle"
      dominantBaseline="middle"
      className={cx({
        [styles.ValueTextExceeded]: isExceeded,
      })}
      style={
        {
          '--gauge-value-font-size': `${mainFontSize}px`,
          '--gauge-sub-value-font-size': `${subFontSize}px`,
        } as CSSProperties
      }
    >
      <tspan x="50%" dy="-0.1em" className={styles.ValueText}>
        {isDash ? '- %' : `${percentage}%`}
      </tspan>
      <tspan x="50%" dy="1.4em" className={styles.ValueSubText}>
        {realizedText}
      </tspan>
    </text>
  );
};

/**
 * Custom red excess ring that appears outside the blue ring when over 100%
 * Uses stroke-dasharray technique for simpler rendering
 */
const ExcessRing = () => {
  const { value, valueMax, outerRadius, cx, cy } = useGaugeState();

  // Only show excess ring if value exceeds valueMax
  // Guard against division by zero when valueMax is 0
  if (
    value === null ||
    valueMax === null ||
    value <= valueMax ||
    valueMax === 0
  ) {
    return null;
  }

  // Calculate radius values
  const maxRadius = Math.min(cx, cy);
  const outerR =
    typeof outerRadius === 'string'
      ? (parseFloat(outerRadius) / 100) * maxRadius
      : outerRadius || maxRadius;

  // Calculate dimensions for the outer excess ring
  const ringThickness = 2; // 3px gap between main ring and excess ring
  const gapWidth = ringThickness + 1;
  // Account for stroke width - reduce by half stroke width to prevent clipping
  const excessRadius = outerR + gapWidth + ringThickness / 2 - 1; // -1px buffer

  // Calculate excess percentage
  // Safe division: valueMax > 0 is guaranteed by the guard above
  const excessRatio = (value - valueMax) / valueMax;
  const progress = Math.min(excessRatio, 1.0); // Cap at 100%

  // Circle path
  const circlePath = `M ${cx},${cy} m ${excessRadius},0 a ${excessRadius},${excessRadius} 0 1 1 ${-2 * excessRadius},0 a ${excessRadius},${excessRadius} 0 1 1 ${2 * excessRadius},0`;

  // Circumference for stroke-dasharray
  const circumference = 2 * Math.PI * excessRadius;

  return (
    <path
      d={circlePath}
      fill="none"
      stroke={scssVariables.ColorCoral80}
      strokeWidth={ringThickness}
      strokeLinecap="round"
      transform={`rotate(-90 ${cx} ${cy})`}
      strokeDasharray={`${circumference} ${circumference}`}
      strokeDashoffset={`${(1 - progress) * circumference}`}
      className={styles.ExcessRing}
    />
  );
};

export const GaugeRealized = ({
  type = 'currency',
  title,
  lang,
  realizedValue,
  plannedValue,
  budgetValue,
  currency,
  width = 100,
  height = 100,
}: GaugeRealizedProps) => {
  const titleId = useId();
  const { t } = useTranslation('charts');

  // Calculate gauge metrics
  const bothZero = budgetValue === 0 && realizedValue === 0;
  const budgetIsZeroButHasRealized = budgetValue === 0 && realizedValue > 0;

  const percentage = bothZero
    ? 0
    : budgetIsZeroButHasRealized
      ? 100
      : Math.round((realizedValue / budgetValue) * 100);
  const isExceeded = realizedValue > budgetValue && !budgetIsZeroButHasRealized;

  // Adjust gauge values for special cases
  const gaugeValue = bothZero || budgetIsZeroButHasRealized ? 0 : realizedValue;
  const gaugeValueMax =
    bothZero || budgetIsZeroButHasRealized ? 1 : budgetValue;

  // Format based on type
  const isCurrency = type === 'currency';
  const isHours = type === 'hours';

  const hoursOptions = useMemo(
    () => ({
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }),
    [],
  );

  // Format values based on type
  const formattedRealized = isCurrency
    ? formatCurrency(realizedValue, lang, currency)
    : formatNumber(realizedValue, lang, hoursOptions);
  const formattedPlanned = isCurrency
    ? formatCurrency(plannedValue, lang, currency)
    : formatNumber(plannedValue, lang, hoursOptions);
  const formattedBudget = isCurrency
    ? formatCurrency(budgetValue, lang, currency)
    : formatNumber(budgetValue, lang, hoursOptions);

  const realizedText = t('gauge.Realized');
  const actualText = t('gauge.Actual');
  const plannedText = t('gauge.Planned');
  const estimatedText = t('gauge.Estimated');

  // Build accessible label for the gauge
  const gaugeAriaLabel = t('gauge.ScreenReaderLabel', {
    title,
    percentage,
    realizedLabel: formattedRealized,
    budgetLabel: formattedBudget,
    exceeded: isExceeded ? ` ${t('gauge.TargetExceeded')}` : '',
  });

  const HoursAbbr = () => (
    <abbr title={t('gauge.Hours')}>{t('gauge.HoursAbbr')}</abbr>
  );

  return (
    <Stack
      alignItems="center"
      gap={0.5}
      role="region"
      aria-labelledby={titleId}
    >
      {/* Title */}
      <Stack alignItems="center" gap={0.25}>
        <Typography
          component="h3"
          variant="bodyRegular"
          color="primary"
          id={titleId}
        >
          {title}
        </Typography>
        <Tooltip title={t('gauge.Budget')}>
          <Typography variant="label" component="p" color="secondary">
            {formattedBudget} {isHours && <HoursAbbr />}
          </Typography>
        </Tooltip>
      </Stack>

      {/* Gauge Chart */}
      <Box className={styles.Wrapper}>
        {/* Screen reader only text with complete information */}
        <Box component="span" className={styles.ScreenReaderText}>
          {gaugeAriaLabel}
        </Box>
        <GaugeContainer
          width={width}
          height={height}
          value={gaugeValue}
          valueMax={gaugeValueMax}
          innerRadius="90%"
          outerRadius="100%"
          cornerRadius="50%"
          aria-hidden="true"
        >
          <GaugeReferenceArc className={styles.ReferenceArc} />
          {realizedValue > 0 && <GaugeValueArc className={styles.ValueArc} />}
          <ExcessRing />
          <GaugeValueText
            isExceeded={isExceeded}
            isDash={budgetIsZeroButHasRealized}
            realizedText={realizedText}
          />
        </GaugeContainer>
      </Box>

      {/* Bottom Labels */}
      <Stack direction="row" gap={4} justifyContent="center" component="dl">
        <Stack
          alignItems="center"
          gap={0}
          direction="column-reverse"
          component="div"
        >
          <Typography variant="label" component="dt">
            {isHours ? actualText : estimatedText}
          </Typography>
          <Typography variant="label" component="dd">
            {formattedRealized} {isHours && <HoursAbbr />}
          </Typography>
        </Stack>
        <Tooltip title={<Trans i18nKey="gauge.PlannedTooltip" ns="charts" />}>
          <Stack
            alignItems="center"
            gap={0}
            direction="column-reverse"
            component="div"
          >
            <Typography variant="label" component="dt" color="secondary">
              {plannedText}
            </Typography>
            <Typography variant="label" color="secondary" component="dd">
              {formattedPlanned} {isHours && <HoursAbbr />}
            </Typography>
          </Stack>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
